import { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, Send, X, Check, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 5000;
const NOTIFY_COOLDOWN_MS = 60_000; // avoid spamming the OS with duplicate pings

// Tiny embedded ping — 0.2s soft bell. Base64 WAV.
const PING_SRC =
  "data:audio/wav;base64,UklGRvQEAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YdAEAAAAAP9/EIw8mNSjKq6GthO85r89wLm+9brYtDCstKKvl/aLtn+Bc31nrVsBUY9HrT+eOX41ZjNlM4E1uzkGQFxIrVLtXhVsD3vDihWbHKytvcHOsN9m8Ojh1tPExsy6/68qpsGdxJZakoiPnI7ClJydb6dss3PBcNBd4EDx6QLXFRcorTgHSDNVEmDDaEBubXG7cRVv1Wl2YSRWtEjnODYnBRQiAA==";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" }); }
  catch { return ""; }
};

export const AdminChats = ({ authAxios }) => {
  const [chats, setChats] = useState([]);
  const [activeSid, setActiveSid] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [notifyOn, setNotifyOn] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("anabiya:notify_on") !== "0"
  );
  const [permission, setPermission] = useState(
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported"
  );
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const lastPingRef = useRef({}); // { [sid]: timestamp }
  const seenCountsRef = useRef({}); // { [sid]: customerMsgCount }
  const isFirstLoadRef = useRef(true);

  // Ensure audio element exists
  useEffect(() => {
    audioRef.current = new Audio(PING_SRC);
    audioRef.current.volume = 0.5;
  }, []);

  const requestPermission = async () => {
    if (!("Notification" in window)) return;
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p === "granted") toast.success("Browser notifications enabled");
      else if (p === "denied") toast.error("Notifications blocked in browser settings");
    } catch (e) { /* noop */ }
  };

  const toggleNotify = () => {
    const next = !notifyOn;
    setNotifyOn(next);
    localStorage.setItem("anabiya:notify_on", next ? "1" : "0");
    if (next && permission === "default") requestPermission();
  };

  const firePing = (chat, lastMsg) => {
    if (!notifyOn) return;
    const now = Date.now();
    const last = lastPingRef.current[chat.session_id] || 0;
    if (now - last < NOTIFY_COOLDOWN_MS) return;
    lastPingRef.current[chat.session_id] = now;
    // Audio
    try { audioRef.current?.play?.().catch(() => {}); } catch (e) { /* noop */ }
    // Browser notification
    if ("Notification" in window && Notification.permission === "granted") {
      try {
        const n = new Notification(`New message — ${chat.name || "Guest"}`, {
          body: (lastMsg?.text || "").slice(0, 140),
          tag: `anabiya-chat-${chat.session_id}`,
          silent: false,
        });
        n.onclick = () => { window.focus(); setActiveSid(chat.session_id); n.close(); };
      } catch (e) { /* noop */ }
    }
  };

  const load = async () => {
    try {
      const { data } = await authAxios().get("/admin/chats");
      const next = data || [];
      // Detect new customer messages by comparing customer-msg counts
      if (!isFirstLoadRef.current) {
        next.forEach((c) => {
          const custCount = (c.messages || []).filter((m) => m.from === "customer").length;
          const prev = seenCountsRef.current[c.session_id];
          if (prev !== undefined && custCount > prev) {
            const lastCustomer = [...(c.messages || [])].reverse().find((m) => m.from === "customer");
            firePing(c, lastCustomer);
          }
          seenCountsRef.current[c.session_id] = custCount;
        });
      } else {
        next.forEach((c) => {
          seenCountsRef.current[c.session_id] =
            (c.messages || []).filter((m) => m.from === "customer").length;
        });
        isFirstLoadRef.current = false;
      }
      setChats(next);
      setActiveSid((prev) => prev || next?.[0]?.session_id || null);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ask for browser notification permission on mount (once)
  useEffect(() => {
    if (notifyOn && permission === "default") requestPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = chats.find((c) => c.session_id === activeSid);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active?.messages?.length, activeSid]);

  const send = async () => {
    if (!reply.trim() || !active || sending) return;
    setSending(true);
    try {
      const { data } = await authAxios().post(`/admin/chats/${active.session_id}/reply`, { message: reply.trim() });
      setChats((list) => list.map((c) => (
        c.session_id === active.session_id
          ? { ...c, messages: [...c.messages, data.message], admin_unread: false, admin_has_replied: true, last_admin_at: data.message.at }
          : c
      )));
      setReply("");
    } catch (e) {
      toast.error("Failed to send");
    } finally { setSending(false); }
  };

  const markRead = async (sid) => {
    try {
      await authAxios().patch(`/admin/chats/${sid}`, { admin_unread: false });
      setChats((list) => list.map((c) => (c.session_id === sid ? { ...c, admin_unread: false } : c)));
    } catch (e) { /* noop */ }
  };

  const openChat = (sid) => {
    setActiveSid(sid);
    const c = chats.find((x) => x.session_id === sid);
    if (c?.admin_unread) markRead(sid);
  };

  if (!chats.length) {
    return (
      <div data-testid="admin-chats-empty" className="p-10 text-center border border-hairline bg-cream">
        <MessageCircle size={26} strokeWidth={1.3} className="text-champagne mx-auto mb-3" />
        <p className="font-display text-forest text-xl">No live chats yet.</p>
        <p className="text-forest/60 text-sm mt-1">Customer messages will land here in real time.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-6 h-[70vh] min-h-[500px]" data-testid="admin-chats">
      {/* List */}
      <aside className="col-span-12 md:col-span-4 border border-hairline overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-hairline flex items-center justify-between bg-cream">
          <span className="overline text-forest/60">{chats.length} conversations</span>
          <button
            onClick={toggleNotify}
            data-testid="admin-notify-toggle"
            title={notifyOn ? "Notifications on" : "Notifications off"}
            aria-label="Toggle notifications"
            className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full transition-colors ${
              notifyOn ? "bg-forest text-champagne" : "bg-white text-forest/50 border border-hairline"
            }`}
          >
            {notifyOn ? <Bell size={11} strokeWidth={1.6} /> : <BellOff size={11} strokeWidth={1.6} />}
            {notifyOn ? "ON" : "OFF"}
          </button>
        </div>
        {notifyOn && permission === "default" && (
          <button
            onClick={requestPermission}
            data-testid="admin-notify-enable"
            className="px-4 py-2 text-xs text-forest bg-champagne/30 border-b border-hairline hover:bg-champagne/50"
          >
            Enable browser notifications →
          </button>
        )}
        <div className="flex-1 overflow-y-auto">
          {chats.map((c) => {
            const on = c.session_id === activeSid;
            const last = c.messages[c.messages.length - 1];
            return (
              <button
                key={c.session_id}
                onClick={() => openChat(c.session_id)}
                data-testid={`chat-item-${c.session_id}`}
                className={`w-full text-left px-4 py-3 border-b border-hairline transition-colors ${
                  on ? "bg-bone" : "hover:bg-cream"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display text-forest text-base truncate">{c.name || "Guest"}</span>
                  {c.admin_unread && (
                    <span data-testid={`chat-unread-${c.session_id}`} className="w-2 h-2 rounded-full bg-champagne shrink-0" />
                  )}
                </div>
                <div className="text-forest/60 text-xs truncate mt-1">
                  {last?.from === "admin" ? "You: " : ""}{last?.text || "—"}
                </div>
                <div className="text-forest/40 text-[10px] font-mono mt-1">{fmt(c.updated_at)}</div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Conversation */}
      <section className="col-span-12 md:col-span-8 border border-hairline flex flex-col">
        {active ? (
          <>
            <header className="px-5 py-4 border-b border-hairline flex items-center justify-between bg-cream">
              <div>
                <div className="font-display text-forest text-xl leading-none">{active.name || "Guest"}</div>
                <div className="text-forest/60 text-xs mt-1">
                  {active.email ? `${active.email} · ` : ""}
                  {active.phone ? `${active.phone} · ` : ""}
                  {active.page_url ? `on ${active.page_url}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {active.whatsapp_ping_url && (
                  <a
                    href={active.whatsapp_ping_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="chat-whatsapp-handoff"
                    className="inline-flex items-center gap-1 text-[10px] tracking-[2px] uppercase text-forest border border-forest/30 px-3 py-1.5 hover:bg-forest hover:text-champagne transition-colors"
                  >
                    Ping on WhatsApp
                  </a>
                )}
                <div className="overline text-forest/50 text-[10px]">{fmt(active.created_at)}</div>
              </div>
            </header>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-white space-y-3">
              {active.messages.map((m) => (
                <div key={m.id} className={`flex ${m.from === "admin" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-snug ${
                      m.from === "admin"
                        ? "bg-forest text-white rounded-tr-sm"
                        : "bg-bone border border-hairline text-forest rounded-tl-sm"
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{m.text}</div>
                    <div className={`overline text-[9px] mt-1 ${m.from === "admin" ? "text-white/50" : "text-forest/40"}`}>
                      {fmt(m.at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <footer className="p-3 border-t border-hairline bg-cream flex items-center gap-2">
              <input
                data-testid="chat-reply-input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type a reply…"
                className="flex-1 bg-white border border-hairline rounded-full px-4 py-2 text-sm text-forest focus:outline-none focus:border-forest"
              />
              <button
                data-testid="chat-reply-send"
                onClick={send}
                disabled={!reply.trim() || sending}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-forest text-white disabled:opacity-40"
                aria-label="Send"
              >
                <Send size={15} strokeWidth={1.8} />
              </button>
            </footer>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-forest/40">Pick a conversation</div>
        )}
      </section>
    </div>
  );
};
